/**
 * Der Ausgangskorb: was ohne Netz geschrieben wird, geht nicht verloren.
 *
 * Ein Reisebericht entsteht unterwegs, und unterwegs ist das Netz weg. Bisher
 * endete das so: Beitrag tippen, senden, Fehlermeldung — und der Text stand
 * nur so lange im Feld, wie die Seite offen war. Ein Neuladen, ein Absturz,
 * ein zu langer Ausflug in eine andere App: weg.
 *
 * Jetzt liegt er in **IndexedDB** und übersteht all das. Gesendet wird, sobald
 * die App wieder offen und online ist.
 *
 * **Was hier nicht geht, und warum:**
 *
 * - **Kein Versand im Hintergrund.** Die Background-Sync-Schnittstelle gibt es
 *   in Safari nicht. Auf iOS ist „sobald die App wieder offen ist" das
 *   Äußerste, was eine Web-App kann — versprochen wird deshalb auch nicht mehr.
 * - **Keine Videos.** Zweihundert Megabyte in IndexedDB sind eine andere
 *   Größenordnung als ein verkleinertes Foto, und der Upload in Stücken kann
 *   ohnehin fortsetzen, wenn die Leitung wiederkommt.
 *
 * Aufbewahrt werden **die Bilder selbst**, nicht ihre ids vom Server. Ein halb
 * hochgeladenes Foto hinterlässt eine id, die der Server nach einem Tag
 * wegräumt (`sweepOrphans`); ein Eintrag, der darauf zeigt, wäre nach einer
 * Nacht kaputt. Beim Senden wird deshalb frisch hochgeladen, und die Reste
 * räumt der Server weg.
 */

import { ApiError, api } from '../api/client'
import { aufnahmeAus, aufnahmeKopf } from './images'
import type { Media, Message } from '../api/types'

const DB = 'travel-broadcast'
const STORE = 'ausgang'

export interface OutboxEntry {
  /** Vergeben von IndexedDB; zugleich die Reihenfolge des Versands. */
  key: number
  /**
   * Was hier wartet — eine Nachricht oder ein Highlight.
   *
   * **Fehlt das Feld, ist es eine Nachricht.** Was beim Umstieg schon im Korb
   * lag, hat es nicht, und dafür die Datenbankversion hochzuzählen hieße, eine
   * Migration für ein Feld zu schreiben, dessen Fehlen dieselbe Auskunft gibt.
   */
  art?: 'nachricht' | 'highlight'
  tripId: number
  /** Gesetzt: eine private Nachricht an genau diesen Menschen. */
  withUser?: number
  body: string
  /**
   * Der Schlüssel, an dem der Server dieselbe Nachricht wiedererkennt.
   *
   * Er wird beim Ablegen vergeben und bleibt über alle Versuche derselbe --
   * genau darin liegt sein Zweck. Ein Versuch kann durchgehen und trotzdem
   * scheitern: die Nachricht ist auf dem Server, die Antwort geht auf dem
   * Rückweg verloren, oder iOS beendet die App zwischen „abgeschickt" und
   * „abgehakt". Beim nächsten Anlauf entsteht dann keine zweite Nachricht.
   */
  clientKey: string
  /**
   * Worauf sich die Nachricht bezieht.
   *
   * Muss mit in den Korb: eine Antwort, die ohne Netz geschrieben wurde und
   * später ohne ihren Anfang herauskommt, hat den halben Sinn verloren -- und
   * zwar genau den, für den es den Bezug gibt.
   */
  replyToId?: number
  replyToMediaId?: number
  /** Die fertig verkleinerten Fotos, wie sie hochgeladen werden sollen. */
  bilder: Blob[]
  /**
   * Was ein wartendes Highlight ausmacht.
   *
   * Ohne Netz entsteht es genau wie mit: Ort (meistens vom Gerät, siehe
   * `lib/ortung.ts`), Zeit, Text, Fotos. Nur der Weg hinaus wartet.
   */
  highlight?: {
    lat: number
    lon: number
    position_source: 'device' | 'photo' | 'map' | 'track'
    title: string
    body: string
    happened_at: string
    time_source?: 'device' | 'photo' | 'manual' | 'track'
    show_in_chat: boolean
    /**
     * Fotos, die es schon zum Server geschafft haben.
     *
     * Der gemischte Fall: zwei Bilder gingen hoch, beim dritten war das Netz
     * weg. Ohne diese Nummern lägen die ersten beiden für immer ohne Highlight
     * herum und würden irgendwann als Reste weggeräumt.
     */
    media_ids?: number[]
  }
  /** Wann geschrieben -- die Sprechblase zeigt diese Zeit, nicht die des Versands. */
  angelegt: string
  versuche: number
  /** Die letzte Absage des Servers, falls es eine gab. */
  problem?: string
}

/**
 * Was zum Ablegen nötig ist.
 *
 * `body` gehört zur Nachricht und fehlt bei einem Highlight -- deshalb hier
 * freiwillig und beim Ablegen mit einer leeren Zeichenkette unterlegt. Ein
 * Highlight hat seinen Text in `highlight.body`.
 */
type Neu = Omit<OutboxEntry, 'key' | 'angelegt' | 'versuche' | 'clientKey' | 'body'> & {
  clientKey?: string
  body?: string
}

let offen: Promise<IDBDatabase> | null = null

function db(): Promise<IDBDatabase> {
  if (offen !== null) return offen

  offen = new Promise<IDBDatabase>((resolve, reject) => {
    const anfrage = indexedDB.open(DB, 1)
    anfrage.onupgradeneeded = () => {
      const datenbank = anfrage.result
      if (!datenbank.objectStoreNames.contains(STORE)) {
        datenbank.createObjectStore(STORE, { keyPath: 'key', autoIncrement: true })
      }
    }
    anfrage.onsuccess = () => resolve(anfrage.result)
    anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB'))
  })

  // Einmal darum bitten, dass iOS diesen Speicher nicht wegräumt. Safari gibt
  // Website-Daten nach längerer Nichtnutzung frei; für eine App auf dem
  // Home-Bildschirm ist das entschärft, aber fragen kostet nichts.
  void navigator.storage?.persist?.().catch(() => undefined)

  return offen
}

function lauf<T>(modus: IDBTransactionMode, arbeit: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then(
    (datenbank) =>
      new Promise<T>((resolve, reject) => {
        const t = datenbank.transaction(STORE, modus)
        const anfrage = arbeit(t.objectStore(STORE))
        anfrage.onsuccess = () => resolve(anfrage.result)
        anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB'))
      }),
  )
}

// --- Melden, wenn sich etwas ändert -----------------------------------------

const hoerer = new Set<() => void>()

export function abonnieren(bei: () => void): () => void {
  hoerer.add(bei)
  return () => hoerer.delete(bei)
}

function melden(): void {
  for (const h of hoerer) h()
}

// --- Ablegen, lesen, wegwerfen ----------------------------------------------

export async function merken(eintrag: Neu): Promise<void> {
  await lauf('readwrite', (store) =>
    store.add({
      ...eintrag,
      body: eintrag.body ?? '',
      clientKey: eintrag.clientKey ?? neuerSchluessel(),
      angelegt: new Date().toISOString(),
      versuche: 0,
    }),
  )
  melden()
}

/**
 * Ein Schlüssel, der zweimal auf der Welt nicht vorkommt.
 *
 * `randomUUID` gibt es nur in einem sicheren Kontext (HTTPS oder localhost);
 * über eine ungesicherte Verbindung im Heimnetz gäbe es sonst gar keinen
 * Schlüssel -- und damit auch keinen Schutz vor der doppelten Nachricht.
 */
export function neuerSchluessel(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

  const zufall = new Uint8Array(16)
  crypto.getRandomValues(zufall)
  return [...zufall].map((z) => z.toString(16).padStart(2, '0')).join('')
}

/** Alle wartenden Einträge, älteste zuerst. */
export async function alle(): Promise<OutboxEntry[]> {
  try {
    const eintraege = await lauf<OutboxEntry[]>('readonly', (store) => store.getAll())
    return eintraege.sort((a, b) => a.key - b.key)
  } catch {
    // Ein Browser ohne IndexedDB (oder mit gesperrtem Speicher) soll die
    // Anwendung nicht anhalten -- dann gibt es eben keinen Korb.
    return []
  }
}

export async function verwerfen(key: number): Promise<void> {
  await lauf('readwrite', (store) => store.delete(key) as unknown as IDBRequest<undefined>)
  melden()
}

// --- Senden ------------------------------------------------------------------

let laeuft = false

/**
 * Schickt raus, was wartet -- der Reihe nach.
 *
 * **Einer nach dem anderen, und beim ersten Netzfehler ist Schluss.** Parallel
 * wäre schneller und falsch: die Reihenfolge der Beiträge ist bei einem
 * Reisebericht eine Aussage, und zwanzig gleichzeitige Fehlversuche auf einer
 * wackligen Leitung sind keine Hartnäckigkeit, sondern Lärm.
 *
 * Eine **Absage des Servers** (abgeschlossene Reise, pausierte Empfängerin)
 * bleibt am Eintrag stehen, statt ihn wegzuwerfen: der Text gehört dem
 * Menschen, der ihn geschrieben hat. Er sieht den Grund an der Sprechblase und
 * entscheidet selbst.
 *
 * @return wie viele tatsächlich rausgegangen sind
 */
export async function senden(): Promise<number> {
  if (laeuft || !navigator.onLine) return 0
  laeuft = true

  let raus = 0
  try {
    for (const eintrag of await alle()) {
      // Ein Eintrag, den der Server schon einmal abgelehnt hat, wird nicht
      // von selbst wieder versucht -- sonst liefe er in dieselbe Wand.
      if (eintrag.problem !== undefined) continue

      try {
        await schicken(eintrag)
        await verwerfen(eintrag.key)
        raus++
      } catch (fehler) {
        const netz = fehler instanceof ApiError && fehler.offline
        await vermerken(eintrag, netz ? undefined : beschreiben(fehler))
        if (netz) break
      }
    }
  } finally {
    laeuft = false
  }

  if (raus > 0) melden()
  return raus
}

/** Einen einzelnen Eintrag noch einmal versuchen -- auf Knopfdruck. */
export async function nochmal(key: number): Promise<void> {
  const eintrag = (await alle()).find((e) => e.key === key)
  if (eintrag === undefined) return

  try {
    await schicken(eintrag)
    await verwerfen(eintrag.key)
  } catch (fehler) {
    const netz = fehler instanceof ApiError && fehler.offline
    await vermerken(eintrag, netz ? undefined : beschreiben(fehler))
  }
  melden()
}

async function schicken(eintrag: OutboxEntry): Promise<unknown> {
  const ids: number[] = []
  for (const bild of eintrag.bilder) {
    // Ort und Zeit stehen noch im Bild -- nur gelesen hat sie beim Einlegen
    // niemand, weil damals nichts hinausging. Ohne diese Zeile käme ein
    // Beitrag aus dem Korb ohne die Angaben an, die derselbe Beitrag mit Netz
    // mitgebracht hätte.
    const media = await api.upload<Media>(
      `/api/trips/${eintrag.tripId}/media`,
      bild,
      undefined,
      aufnahmeKopf(await aufnahmeAus(bild)),
    )
    ids.push(media.id)
  }

  // Ein Highlight geht denselben Weg wie eine Nachricht: erst die Fotos, dann
  // der Eintrag mit ihren Nummern. Der `client_key` ist dabei nicht Beiwerk --
  // ein Versuch kann durchgehen, während die Antwort verlorengeht, und ohne
  // ihn stünde der Punkt danach zweimal auf der Karte.
  if (eintrag.art === 'highlight' && eintrag.highlight !== undefined) {
    return api.post(`/api/trips/${eintrag.tripId}/highlights`, {
      ...eintrag.highlight,
      media_ids: [...(eintrag.highlight.media_ids ?? []), ...ids],
      client_key: eintrag.clientKey,
    })
  }

  return api.post<Message>(
    `/api/trips/${eintrag.tripId}/messages`,
    {
      body: eintrag.body,
      media_ids: ids,
      client_key: eintrag.clientKey,
      reply_to_id: eintrag.replyToId,
      reply_to_media_id: eintrag.replyToMediaId,
    },
    { with: eintrag.withUser },
  )
}

async function vermerken(eintrag: OutboxEntry, problem: string | undefined): Promise<void> {
  await lauf('readwrite', (store) =>
    store.put({ ...eintrag, versuche: eintrag.versuche + 1, problem }),
  )
  melden()
}

function beschreiben(fehler: unknown): string {
  return fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.'
}
